.button {
  --btn-bg: var(--wb-color-raised);
  --btn-fg: var(--wb-color-text);
  --btn-border: var(--wb-color-outline);
  --btn-bg-hover: var(--wb-color-hover);
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--wb-space-2);
  min-height: var(--wb-hit-min);
  padding: 0 var(--wb-space-4);
  font: var(--wb-font-display-s);
  color: var(--btn-fg);
  text-decoration: none;
  white-space: nowrap;
  background: var(--btn-bg);
  border: var(--wb-border-w) solid var(--btn-border);
  border-radius: var(--wb-radius-m);
  box-shadow: var(--wb-elev-2);
  transition:
    transform var(--wb-dur-instant) steps(2, end),
    box-shadow var(--wb-dur-instant) steps(2, end),
    background-color var(--wb-dur-fast);
  user-select: none;
  touch-action: manipulation;
}

.button:hover {
  background: var(--btn-bg-hover);
  color: var(--btn-fg);
}

.button:active:not(.softDisabled, :disabled) {
  transform: translateY(2px);
  box-shadow: 0 2px 0 #000;
  animation: press-flash 120ms steps(2, end);
}

/* Press feel (§4.7): the button drops in two frames and flashes bright for one. */
@keyframes press-flash {
  0% {
    filter: brightness(1.25);
  }
  100% {
    filter: none;
  }
}

.size-l {
  min-height: var(--wb-hit-primary);
  padding: 0 var(--wb-space-5);
}
.size-s {
  min-height: 36px;
  padding: 0 var(--wb-space-3);
  font: var(--wb-font-tab);
  box-shadow: var(--wb-elev-1);
}
.size-s::after {
  /* Keep a 44px hit area even when the visual is smaller. */
  content: '';
  position: absolute;
  inset: -4px 0;
}

.block {
  display: flex;
  width: 100%;
}

.label {
  overflow: hidden;
  text-overflow: ellipsis;
}

.meta {
  color: inherit;
  opacity: 0.85;
  font-weight: 500;
}

.primary {
  --btn-bg: var(--wb-color-accent);
  --btn-bg-hover: var(--wb-color-accent-strong);
  --btn-fg: var(--wb-color-on-accent);
  --btn-border: #000;
}

.secondary {
  --btn-border: var(--wb-color-outline);
}

.danger {
  --btn-fg: var(--wb-color-danger-text);
  --btn-border: var(--wb-color-danger);
}

.danger-fill {
  --btn-bg: var(--wb-color-danger);
  --btn-bg-hover: #ff8a80;
  --btn-fg: #000;
  --btn-border: #000;
}

.ghost {
  --btn-bg: transparent;
  --btn-bg-hover: transparent;
  --btn-fg: var(--wb-color-accent);
  --btn-border: transparent;
  box-shadow: none;
  padding: 0 var(--wb-space-2);
}
.ghost:hover {
  text-decoration: underline;
  color: var(--wb-color-accent-strong);
}
.ghost:active:not(.softDisabled, :disabled) {
  transform: none;
  box-shadow: none;
}

/* Disabled (§5.1): no opacity (it turned the accent into muddy olive). A flat track plate with
   readable text-3 (6:1), a dashed outline and no shadow — clearly "not yet", still legible. */
.softDisabled:not([aria-busy='true']),
.button:disabled {
  --btn-bg: var(--wb-color-track);
  --btn-bg-hover: var(--wb-color-track);
  --btn-fg: var(--wb-color-text-3);
  border-style: dashed;
  border-color: var(--wb-color-outline);
  box-shadow: none;
  cursor: not-allowed;
}
.ghost.softDisabled:not([aria-busy='true']),
.ghost:disabled {
  --btn-bg: transparent;
  --btn-bg-hover: transparent;
  border-color: transparent;
}
.softDisabled[aria-busy='true'] {
  opacity: 0.85;
  cursor: progress;
}

.iconButton {
  position: relative;
  display: inline-grid;
  place-items: center;
  flex: none;
  width: var(--wb-hit-min);
  height: var(--wb-hit-min);
  padding: 0;
  color: var(--wb-color-text);
  background: var(--wb-color-raised);
  border: var(--wb-border);
  border-radius: var(--wb-radius-m);
  box-shadow: var(--wb-elev-2);
  transition:
    transform var(--wb-dur-instant) steps(2, end),
    box-shadow var(--wb-dur-instant) steps(2, end),
    background-color var(--wb-dur-fast);
  touch-action: manipulation;
}
.iconButton:hover {
  background: var(--wb-color-hover);
}
.iconButton:active:not(:disabled) {
  transform: translateY(2px);
  box-shadow: 0 2px 0 #000;
  animation: press-flash 120ms steps(2, end);
}
.iconButton:disabled {
  color: var(--wb-color-text-muted);
  background: var(--wb-color-track);
  border-style: dashed;
  border-color: var(--wb-color-outline);
  box-shadow: none;
  cursor: not-allowed;
}

.icon-plain {
  background: transparent;
  border-color: transparent;
  box-shadow: none;
}
.icon-plain:hover {
  background: var(--wb-color-raised);
}
.icon-plain:active {
  transform: none;
  box-shadow: none;
}

.icon-accent {
  color: var(--wb-color-on-accent);
  background: var(--wb-color-accent);
}
.icon-accent:hover {
  background: var(--wb-color-accent-strong);
}

.badge {
  position: absolute;
  top: -8px;
  right: -8px;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  display: grid;
  place-items: center;
  font: 700 0.75rem/1 var(--wb-family-body);
  color: #000;
  background: var(--wb-color-danger);
  border: 2px solid #000;
  border-radius: var(--wb-radius-pill);
}

@media (prefers-reduced-motion: reduce) {
  :global(:root:not([data-motion='full'])) .button:active,
  :global(:root:not([data-motion='full'])) .iconButton:active {
    animation: none;
  }
}
:global(:root[data-motion='reduce']) .button:active,
:global(:root[data-motion='reduce']) .iconButton:active {
  animation: none;
}
